import React, { useState } from 'react';

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('first-name', formData.firstName);
      formDataToSend.append('last-name', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3ljv3i96ie05rb9mak0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="bg-gray-900 text-white p-12 rounded-lg">
            <h2 className="text-3xl font-bold mb-8">Get in Touch</h2>

            {/* Headquarters */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-emerald-400">HQ : Bangalore, India</h3>
              <div className="space-y-2 text-gray-300">
                <p>No.34, #1207/343, 9th Main, 7th Sector,</p>
                <p>HSR Layout, Bangalore, Karnataka, 560102</p>
                <p className="flex items-center mt-3">
                  <i className="ri-phone-line mr-2"></i>
                  +91 9994988898
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:holidays@primemeridiantravel.in" className="hover:text-emerald-400 transition-colors">
                    holidays@primemeridiantravel.in
                  </a>
                </p>
              </div>
            </div>

            {/* Malaysia Office */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-emerald-400">Malaysia Office</h3>
              <div className="space-y-2 text-gray-300">
                <p>10-2, Jalan PJS 8/12A, Dataran Mentari,</p>
                <p>46150 Petaling Jaya, Selangor Darul Ehsan</p>
                <p className="flex items-center mt-3">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:holidays@primemeridiantravel.in" className="hover:text-emerald-400 transition-colors">
                    holidays@primemeridiantravel.in
                  </a>
                </p>
              </div>
            </div>

            {/* Dubai Office */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-emerald-400">Dubai Office</h3>
              <div className="space-y-2 text-gray-300">
                <p>601, AL Fardan, Deira, Dubai 500001</p>
                <p className="flex items-center mt-3">
                  <i className="ri-phone-line mr-2"></i>
                  +91 9994988898
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:holidays@primemeridiantravel.in" className="hover:text-emerald-400 transition-colors">
                    holidays@primemeridiantravel.in
                  </a>
                </p>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold mb-4 text-emerald-400">Follow Us</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/primemeridian.travel/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors"
                >
                  <i className="ri-instagram-line text-white"></i>
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61572538649614"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors"
                >
                  <i className="ri-facebook-line text-white"></i>
                </a>
                <a
                  href="https://in.linkedin.com/company/primemeridiantravel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors"
                >
                  <i className="ri-linkedin-line text-white"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-12 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Send us a Message</h2>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  required
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors resize-none"
                  placeholder="Tell us about your travel plans..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 text-white py-4 px-6 rounded-lg hover:bg-gray-800 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </span>
                ) : (
                  'Send Message'
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    <i className="ri-check-line mr-2"></i>
                    Thanks for submitting! We'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-20 text-center">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Choose PrimeMeridian Travel?</h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              With over 13 years of experience in crafting extraordinary travel experiences, we specialize in creating personalized journeys that exceed expectations. Our team of travel experts is dedicated to making your dream vacation a reality.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-2xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">24/7 Support</h4>
                <p className="text-gray-600 text-sm">Round-the-clock assistance for all your travel needs</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Trusted Service</h4>
                <p className="text-gray-600 text-sm">13+ years of reliable travel planning experience</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Global Reach</h4>
                <p className="text-gray-600 text-sm">Offices in India, Malaysia, and Dubai</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};